import React from 'react';
import {
  Component,
  Position,
  PuzzleButton,
  Container
} from "puzzle";
import { WORKFLOW } from "../../Types";
import { getUnixDate } from "puzzle/utility/Util";
import _ from "lodash";
import SourceFiles from "./SourceFiles/SourceFiles";
import BatchesContent from "./Batches/BatchesContent";
import { orderBy } from '@progress/kendo-data-query';
import { Keys } from "@progress/kendo-react-common";
import { adjustPhaseDueDates } from "../../../util/Util";
import { DUE_DATE_INFO, WORKFLOW_DATA } from "../../MultiBatchUpload/Section/Types";


export type PROPS = {
  form: any,
  isTemplate: boolean,
  fileUploadInProgress: boolean,
  partiallyAnalyzed: boolean,
  projectPolicies: any,
  submissionMode: string,
  submissionStatus: string,
  uploadFlowState: any
};

export type STATE = {
  batchInfos: any[],
  deletedBatches: [],
  projectFileFormats: [],
  initializeUserData: any,
  submissionStarted: boolean
  workflows: WORKFLOW[],
}

class Content extends Component<PROPS, STATE> {

  submissionId: string = '';
  partiallyAnalyzed = false;
  static defaultProps = {
    isTemplate: false
  };

  public state: STATE = {
    batchInfos: [],
    deletedBatches: [],
    projectFileFormats: [],
    initializeUserData: {},
    workflows: [],
    submissionStarted: false,
  };

  contentWrapperRef: any;

  /**
   * If user deletes all batches except one (created through multi batch dialog) sets files from that batch to simple batch.
   */
  addFilesToSimpleBatch = () => {
    const { batchInfos } = this.state;

    if (batchInfos.length === 1) {
      let { targets = [], uploadedFiles = [], submissionFolder = '' } = batchInfos[0];
      if (targets?.length > 0 && !uploadedFiles?.length) {
        this.dispatch('setExistingFiles', Position.end, {
          targets,
          details: { project: this.props.form.getData().project },
          moduleId: 'fromContent'
        });
      } else {
        uploadedFiles = uploadedFiles.map((item: any) => {
          item.isAlreadyUpload = true;
          return item;
        })
        this.dispatch('updateState', Position.end, {
          uploadedFiles,
          submissionFolder,
          moduleId: 'fromContent'
        });
      }

      setTimeout(() => {
        this.dispatch('ignoreDueDateOnDetails', Position.end);
      }, 0);
    }
  };

  /**
   * Checks if human steps exist in workflow(s)
   *
   * The method first finds all selected workflow ids. Then, using the reduce method,
   * it iterates through the entire list of workflows and finds the corresponding objects of the selected workflows matching ids.
   * When it finds the appropriate workflow, it adds the length of the phase to the phaseCount, which we ultimately use to determine if at least one human phase on the selected workflows exists.
   * Note: human phase exists if phases props on the workflow has length (not empty)
   */
  checkHumanSteps = () => {
    const { workflows, batchInfos } = this.state;
    let workflowIds = [this.props.form.getData().workflow];
    if (batchInfos.length > 1) {
      workflowIds = batchInfos.map((currentBatch: any) => {
        return currentBatch.workflowId;
      });
    }
    let phasesCount = workflows.reduce((phasesCount: number, wf: any) => {
      if (workflowIds.includes(wf.id)) {
        phasesCount += wf.phases ? wf.phases.length : this.getHumanPhasesCountForChangedWf(wf.id);
      }
      return phasesCount;
    }, 0);
    return !!phasesCount
  }

  /**
   * Only executes when submission is in EDIT or CREATE mode, on submission dueDate change sets batchDueDate value.
   * @param dueDate
   */
  changeSimpleSubmissionBatchDueDate = (dueDate: number): void => {
    let { batchInfos } = this.state;

    if (batchInfos[0]) {
      const newBatchItem = {
        ...batchInfos[0],
        dueDate,
        targetLanguageInfos: batchInfos[0].targetLanguageInfos?.map(this.mapDueDateForCurrentTargetLanguage.bind(this, dueDate))
      };
      this.setState({ batchInfos: [newBatchItem] })
    }
  };

  /**
   * Creates new targetLanguageInfos for SimpleBatch
   * @param targetLanguages
   */
  changeSimpleSubmissionTargetLanguageInfos = (targetLanguages: any[]) => {
    const { batchInfos } = this.state;
    const date = getUnixDate(this.props.form.getFields().specificDate.value);
    const dueDateInfos = batchInfos[0].targetLanguageInfos?.map((item: any) => {
      return {
        phasesDueDates: item.dueDateInfos,
        targetLanguage: item.targetLanguage
      }
    }) || [];

    const targetLanguageInfos = targetLanguages.map(this.mapTargetLanguages.bind(this, date, dueDateInfos));
    const batchItem = {
      ...batchInfos[0],
      targetLanguageInfos
    };
    this.setState({ batchInfos: [batchItem] });
  };

  /**
   * Only executes when submission is in EDIT or CLONE mode, on submission workflow change updates batchInfos value.
   * @param workflowData
   */
  changeEditSubmissionWorkflow = (workflowData: WORKFLOW_DATA) => {
    const { batchInfos } = this.state;
    const { workflowName, workflowId } = workflowData
    const newBatchItem = {
      ...batchInfos[0],
      workflowId,
      workflowName,
      targetLanguageInfos: batchInfos[0].targetLanguageInfos ?
        batchInfos[0].targetLanguageInfos.map((item: any) => {
          return { ...item, dueDateInfos: [] }
        }) : []
    }

    this.setState({
      batchInfos: [newBatchItem]
    })
  }

  /**
   * Set uploaded files into the state for POST
   */
  dropUploadChange = (uploadedFiles: any, deletedFiles: any) => {
    this.props.form.setData({ uploadedFiles: _.cloneDeep(uploadedFiles), deletedFiles: _.cloneDeep(deletedFiles) });
  };

  /**
   * BatchInfos length getter
   */
  getBatchInfosLength = () => {
    return this.state.batchInfos?.length || 1;
  };

  /**
   * Get data for post request
   */
  getData = () => {
    const { submissionMode } = this.props;
    const { batchInfos, deletedBatches } = this.state;
    const data = this.state.batchInfos.length < 2 ? this.getSimpleBatchData() : batchInfos;
    let contendData;
    if (submissionMode === 'EDIT' || submissionMode === 'TEMPLATE_EDIT') {
      contendData = {
        batchInfos: data,
        deletedBatches
      }
    } else {
      contendData = data
    }

    return contendData;
  };

  /**
   * File formats getter
   */
  getFileFormat = () => this.state.projectFileFormats;

  /**
   * Returns length of changed wf from dueDateInfos, since there are no data for phases of changed wf
   *
   * @param workflowId
   */
  getHumanPhasesCountForChangedWf = (workflowId: number): number => {
    const { batchInfos } = this.state;
    const batch = batchInfos.find((currentBatch: any) => currentBatch.workflowId === workflowId);
    return batch?.targetLanguageInfos[0].dueDateInfos?.length || 0;
  };

  /**
   * Returns all necessary data for opening multiBatchDialog
   */
  getMultiBatchDialogConfiguration = () => {
    const { workflows, batchInfos, projectFileFormats, submissionStarted, deletedBatches } = this.state;
    const { specificDate, paClient = undefined, project, sourceLanguage } = this.props.form.getData();
    const { projectPolicies, submissionMode, submissionStatus } = this.props;
    const isTemplate = submissionMode === 'TEMPLATE_CREATE' || submissionMode === 'TEMPLATE_EDIT';
    const hasHumanSteps: boolean = this.checkHumanSteps();

    return {
      batchInfos,
      deletedBatches,
      dialogSize: isTemplate ? 'md multi-batch' : 'xxl multi-batch',
      hasHumanSteps,
      paClient,
      partiallyAnalyzed: this.partiallyAnalyzed,
      project,
      projectFileFormats,
      projectPolicies,
      simpleBatchData: this.getSimpleBatchData(),
      sourceLanguage,
      submissionDueDate: specificDate,
      submissionId: this.submissionId,
      submissionMode,
      submissionStarted,
      submissionStatus,
      workflows
    }
  };

  /**
   * Gets all data related to simple batch
   */
  getSimpleBatchData = () => {
    let {
      batchId,
      workflow,
      uploadedFiles,
      deletedFiles,
      targetFormat,
      targetLanguage,
      specificDate
    } = this.props.form.getData();
    return {
      workflow,
      uploadedFiles,
      batchId,
      deletedFiles,
      targetFormat,
      targetLanguage,
      targetLanguageInfos: this.state.batchInfos[0]?.targetLanguageInfos,
      specificDate,
      dueDate: this.state.batchInfos[0]?.dueDate || this.state.batchInfos[0]?.targetLanguageInfos[0]?.dueDate,
      phasesDueDate: this.state.batchInfos[0]?.targetLanguageInfos,
      name: this.state.batchInfos[0]?.name
    };
  };

  /**
   * Workflow getter
   */
  getWorkflowData = () => {
    return this.state.workflows;
  };

  /**
   * Return a workflow if it exists on a project
   */
  getWorkflowValue = (workflowId: number): WORKFLOW | undefined => {
    return _.find(this.state.workflows, { 'id': workflowId });
  };

  /**
   * Map due date info
   */
  mapDueDateForCurrentTargetLanguage = (dueDate: number, currentTargetLanguageInfos: any) => {
    let newLangInfo = {
      ...currentTargetLanguageInfos,
      dueDate
    };
    let oldDueDate = currentTargetLanguageInfos.dueDate;

    if (oldDueDate > dueDate) {
      newLangInfo = {
        ...newLangInfo,
        dueDateInfos: adjustPhaseDueDates(currentTargetLanguageInfos.dueDateInfos, dueDate)
      }
    } else {
      let dueDateInfos = currentTargetLanguageInfos.dueDateInfos;
      let dueDateInfosLength = dueDateInfos.length;
      let lastIndex = dueDateInfosLength - 1;
      dueDateInfos[lastIndex] = {
        ...dueDateInfos[lastIndex],
        dueDate: dueDate
      };
      newLangInfo = {
        ...newLangInfo,
        dueDateInfos
      }
    }

    return newLangInfo;
  };

  /**
   * Maps targetLanguages to proper form
   */
  mapTargetLanguages = (dueDate: any, dueDateInfos: any[], targetLanguageInfo: any) => {
    let findDueDate;
    if (dueDateInfos.length) {
      findDueDate = dueDateInfos.find((item: any) => item?.targetLanguage === (targetLanguageInfo?.targetLanguage || targetLanguageInfo));
    }
    return {
      dueDate,
      targetLanguage: targetLanguageInfo?.targetLanguage || targetLanguageInfo,
      tmProfileId: targetLanguageInfo?.tmProfileId || null,
      dueDateInfos: findDueDate?.phasesDueDates || [],
      totalWordCount: 0
    }
  };

  /**
   * Opens content popup
   */
  openContentPopup = () => {
    const { batchInfos, projectFileFormats, submissionStarted } = this.state;
    const uploadedFiles = this.props.form.getData().uploadedFiles.map((item: any) => {
      return {
        ...item,
        isAlreadyUpload: true
      }
    });
    this.dispatch('openSubmissionPopup', '', {
      popupName: 'content',
      contentProps: {
        ...this.props,
        partiallyAnalyzed: this.partiallyAnalyzed,
        uploadedFiles,
        submissionStarted,
        projectFileFormats,
        batchInfos,
        submissionId: this.submissionId
      },
    });
  };

  /**
   * Opens multi batch dialog
   */
  openMultiBatchDialog = () => {
    if (this.props.projectPolicies?.hasEditDueDatePolicy || !_.includes(['CREATE', 'CLONE'], this.props.submissionMode)) {
      const dialogConfiguration = this.getMultiBatchDialogConfiguration();
      this.dispatch('openMultiBatch', '', dialogConfiguration)
    } else {
      this.dispatch('noDueDateWarning')
    }
  };

  /**
   * Prevent dialog open when button is disabled
   */
  organizeIntoBatchesKeyDownHandler = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    let keyCode = event.keyCode;

    if (keyCode === Keys.enter || keyCode === Keys.space) {
      // @ts-ignore
      let ariaDisabled = event.target.getAttribute('aria-disabled');
      if (ariaDisabled === 'true') {
        event.preventDefault();
      }
    }
  }

  /**
   * Reset project related data
   */
  resetProjectRelatedData = () => {
    let formData = {
      workflow: null
    };
    this.props.form.setData(formData);

    let stateData = {
      workflows: []
    };
    this.setState(stateData)
  };

  /**
   * Check if workflow exist
   * When workflow is edited, id is changed and saved workflow cannot be accessed anymore.
   * @param workflowId
   */
  checkIfWorkflowExist = (workflowId: number) => {
    const { workflows } = this.state;
    let workflowExist = _.find(workflows, { id: workflowId });

    return workflowExist;
  }

  /**
   * BatchInfos setter
   * @param batchData
   */
  setBatchInfosData = (batchData: any) => {
    let { batchInfos, deletedBatches } = batchData;
    let workflowId = batchInfos[0].workflowId;
    if (!this.checkIfWorkflowExist(workflowId)) {
      workflowId = null;
    }
    if (batchInfos.length === 1) {
      //TODO Optimise this part
      switch (this.props.submissionMode) {
        case 'TEMPLATE_EDIT':
          this.props.form.setData({
            isMultiBatch: false,
            batchId: batchInfos[0].batchId,
            workflow: workflowId,
            targetLanguage: batchInfos[0].targetLanguageInfos?.map((item: any) => item.targetLanguage)
          });
          break;
        case 'TEMPLATE_CREATE':
          this.props.form.setData({
            isMultiBatch: false,
            workflow: workflowId,
            targetLanguage: batchInfos[0].targetLanguageInfos?.map((item: any) => item.targetLanguage)
          });
          break;
        default:
          this.props.form.setData({
            batchId: batchInfos[0].batchId,
            uploadedFiles: _.cloneDeep(batchInfos[0].uploadedFiles) || _.cloneDeep(batchInfos[0].targets),
            deletedFiles: _.cloneDeep(batchInfos[0].deletedFiles),
            isMultiBatch: false,
            batchesWithFiles: false,
            submissionFolder: batchInfos[0].submissionFolder,
            workflow: workflowId,
            targetLanguage: batchInfos[0].targetLanguageInfos?.map((item: any) => item.targetLanguage)
          });
          break;
      }
    } else {
      const batchesWithFiles = batchInfos.every((item: any) => !!item.fileCount || !!item.targets?.length || !!item.droppedFiles?.length);
      this.props.form.setData({
        workflow: workflowId,
        isMultiBatch: true,
        ...(!['TEMPLATE_EDIT', 'TEMPLATE_CREATE'].includes(this.props.submissionMode) && { batchesWithFiles })
      });
    }

    let ignoreFields = batchInfos.length > 1 ? ['workflow'] : [];
    this.props.form.ignoreFields(ignoreFields);

    this.setState({
      batchInfos,
      deletedBatches
    }, this.addFilesToSimpleBatch);
  };

  /**
   * Set calculated due dates for simple batch
   */
  setCalculatedDueDates = (calculatedDueDates: DUE_DATE_INFO[]) => {
    let { batchInfos } = this.state;
    if (batchInfos.length === 1) {
      let newBatch = { ...batchInfos[0] };
      newBatch = {
        ...newBatch,
        targetLanguageInfos: newBatch.targetLanguageInfos.map((item: any) => {
          return {
            ...item,
            dueDateInfos: calculatedDueDates
          }
        })
      }
      this.setState({
        batchInfos: [newBatch]
      });
    }
  }

  /**
   * Set data
   */
  setData = (data: any, details: any, initializeUserData: any, partiallyAnalyzed = false, afterTemplateChange: boolean = false) => {
    //only one batch for phase 1
    this.partiallyAnalyzed = partiallyAnalyzed && _.includes(['EDIT'], this.props.submissionMode);
    let batch = data.batchInfos[0];
    let workflow = this.getWorkflowValue(batch.workflowId);
    const submissionStarted = data.started;
    let workflows = this.state.workflows;

    if (submissionStarted && !workflow) {
      workflows.push({
        description: null,
        id: batch.workflowId,
        name: batch.workflowName
      });
    }

    const onlyOneWorkflow = workflows.length === 1;
    if (onlyOneWorkflow) {
      workflow = workflows[0];
    }

    const batchInfos = data.batchInfos.map((item: any, index: number) => {
      return {
        ...item,
        moduleId: 'MultiBatchUpload_' + index
      }
    });

    this.submissionId = data.submissionId;
    this.setState({
      initializeUserData,
      submissionStarted,
      workflows,
      batchInfos
    }, () => {
      let formControlData: any = {
        workflow: submissionStarted ? batch.workflowId : workflow?.id,
        batchId: batch.batchId,
        targetFormat: batch.targetFormat,
        isMultiBatch: batchInfos?.length > 1
      };

      if (this.props.submissionMode !== 'TEMPLATE_EDIT') {
        let batchesWithFiles = batchInfos?.every((item: any) => !!item.targets?.length);
        formControlData = {
          ...formControlData,
          batchesWithFiles
        }
      }
      if (workflow?.id && details.project) {
        this.dispatch('updateDetailsWithWorkflowState', Position.end, {
          workflow
        })
      }
      this.props.form.setData(formControlData);
    });
  };

  /**
   * File format setter
   * @param projectFileFormats
   */
  setFileFormat = (projectFileFormats: any) => {
    this.setState({
      projectFileFormats
    });
  };

  /**
   * Initial user setter. Fires on create submission only
   *
   * @param initializeUserData
   */
  setInitialUserData = (initializeUserData: any) => {
    this.setState({ initializeUserData });
  };

  /**
   * Set workflow data
   */
  setWorkflowData = (workflows: WORKFLOW[] = [], defaultWorkflowId: number) => {
    const workflow = this.getWorkflowValue(defaultWorkflowId);
    this.setState({
      workflows: orderBy(workflows, [{
        field: 'name',
        dir: 'asc'
      }])
    });
    if (workflow?.id && _.map(workflows, 'id').includes(workflow.id)) {
      this.props.form.setData({ workflow: workflow?.id });
    }
  };

  renderBody() {
    let { batchInfos, projectFileFormats } = this.state;
    const {
      isTemplate,
      submissionStatus,
      fileUploadInProgress,
      uploadFlowState,
      submissionMode,
      projectPolicies
    } = this.props;
    const { project, specificDate, sourceLanguage, targetLanguage } = this.props.form.getFields();
    const containerContentClassname = `tp-section-container-content-inner-wrap ${isTemplate && this.state.batchInfos.length < 2 ? 'k-display-none' : ''}`;
    const regularCondition = project?.value && !fileUploadInProgress && sourceLanguage?.value && targetLanguage?.value.length > 0;
    const organizeContentIntoBatchesFlag = regularCondition && (_.includes(['TEMPLATE_CREATE', 'TEMPLATE_EDIT'], submissionMode) || specificDate?.value || (_.includes(['CREATE', 'CLONE'], submissionMode) && projectPolicies && !projectPolicies.hasEditDueDatePolicy));
    return (
      <Container
        ariaLabel={this.translate('SRL-section')}
        displayFlex
        className={'tp-section-container k-widget tp-content-section tp-100'}
        ref={wrapper => this.contentWrapperRef = wrapper}
        htmlTag={'section'}
        tabIndex={0}
      >
        <Container className='tp-section-shadow' flex1>
          <Container
            className={`tp-section-container-content`}
            vBox
          >
            <Container
              className={'tp-section-content-header'}
              justifyContentBetween
              hBox
              wrap
            >
              <Container hBox>
                <Container
                  htmlTag={'h3'}
                  hBox
                  alignItemsCenter
                  justifyContentBetween
                  className="tp-section-container-title tp-100 tp-font-size-md"
                >
                  {this.translate(('title'))}
                </Container>
                {!isTemplate && batchInfos.length < 2 && (
                  <PuzzleButton
                    aria-label={this.translate('SRL-openContentInModal')}
                    titleText={this.translate('SRL-openContentInModal')}
                    className={`${project?.value ? '' : 'k-disabled'}`}
                    disabled={!project?.value}
                    onClick={this.openContentPopup}
                    iconClass={'tp-icon tp-open_in_new'}
                    fillMode='flat'
                    themeColor={'primary'}
                    tooltipPosition={'right'}
                    withTooltip />
                )}
              </Container>
              <PuzzleButton
                aria-disabled={!organizeContentIntoBatchesFlag}
                className={organizeContentIntoBatchesFlag ? '' : 'k-disabled'}
                onClick={this.openMultiBatchDialog}
                onKeyDown={this.organizeIntoBatchesKeyDownHandler}
                fillMode={'outline'}
                themeColor={'primary'}
              >
                {batchInfos.length < 2 ? this.translate('organizeIntoBatches') : this.translate('editOrAdd')}
              </PuzzleButton>
            </Container>
            <div className={containerContentClassname}>
              {(batchInfos.length < 2) && <>
                <SourceFiles
                  batch={batchInfos[0]}
                  onChange={this.dropUploadChange}
                  moduleId={'fromContent'}
                  isDisabled={isTemplate}
                  submissionId={this.submissionId}
                  submissionStarted={this.state.submissionStarted}
                  submissionStatus={submissionStatus}
                  partiallyAnalyzed={this.partiallyAnalyzed}
                  projectId={project?.value}
                  selectionField={'selected'}
                  projectFileFormats={projectFileFormats}
                  uploadFlowState={uploadFlowState}
                />
              </>
              }
              {(batchInfos.length >= 2) &&
                <BatchesContent
                  initializeUserData={this.state.initializeUserData}
                  changeData={this.setBatchInfosData}
                  form={this.props.form}
                  partiallyAnalyzed={this.partiallyAnalyzed}
                  projectPolicies={this.props.projectPolicies}
                  submissionDueDate={getUnixDate(specificDate?.value || new Date())}
                  submissionMode={this.props.submissionMode}
                  submissionStarted={this.state.submissionStarted}
                  submissionStatus={submissionStatus}
                  hasHumanSteps={this.checkHumanSteps()}
                />
              }
            </div>
            <SourceFiles
              batch={batchInfos[0]}
              className={'MultiBatchUpload_0'}
              hideDialog={true}
              isCollapsed={true}
              isDisabled={false}
              hasFileConnection={true}
              moduleId={'MultiBatchUpload_0'}
              multiBatchDialog={true}
              partiallyAnalyzed={this.props.partiallyAnalyzed}
              projectFileFormats={projectFileFormats}
              projectId={project}
              selectionField={'selected'}
              submissionStarted={false}
              submissionStatus={submissionStatus}
              uploadFlowState={uploadFlowState}
            />
          </Container>
        </Container>
      </Container>
    );
  }
}

export default Content;
